/**
 * How a claim reached the person it is credited to (reviews #394, #405), read off
 * Claim detail's rows. Nothing here decides anything: the model attributed every claim
 * (FCC nbBuildCalcClaimAttribution, nbBuildCalcOpenClaimsMonthly) and the build copied
 * what it decided (scripts/build_bundle.py predicted_attribution, open_trails,
 * expected_periods). This file puts it into words. References/MEASURE-RULES.md §12.9 is the rule of record.
 */
import type { ClaimDetailData } from './claimDetail'

/** The rule that credited a billed claim: claims.json's `ab`. */
export type AttributionRule = 'predicted' | 'owner' | 'primary' | 'left-next' | 'left-kept' | 'other'

export const RULE_TEXT: Record<AttributionRule, { short: string; long: string }> = {
  predicted: {
    short: 'Predicted worklist',
    long:
      'Credited to the person assigned to the predicted worklist. The matcher found the worklist whose setup (facility, payer type, service type and plan) covered the claim at the end of its bill month, and took the person assigned to it then.',
  },
  owner: {
    short: 'No prediction: worklist owner',
    long:
      'The matcher predicted nobody for this claim, so it is credited to the owner of the first real worklist (not a system list or a holding queue) it was actually put on.',
  },
  primary: {
    short: 'Followed the primary claim',
    long:
      'A co-insurance claim is credited to the owner of its primary claim: the same patient, facility and service month.',
  },
  'left-next': {
    short: 'Person had left: next holder',
    long:
      'The person it would have gone to had left by the bill date, so it is credited to the next person who held the claim on a real worklist.',
  },
  'left-kept': {
    short: 'Person had left: nobody since',
    long:
      'The person it went to had left by the bill date, and nobody has held the claim on a real worklist since, so it stays with them.',
  },
  other: {
    short: 'Another rule',
    long: 'The model credited it by a rule this build does not name.',
  },
}

/** One run of month ends on the same worklist with the same holder: an `ot` entry. */
export interface TrailRun {
  from: string
  to: string
  worklist: string
  worklistId: number | null
  holder: string
  system: boolean
  /** '' as of; else the model moved it for a holder who had left. */
  rule: '' | 'left-next' | 'left-kept'
}

export function parseTrail(text: string | undefined | null): TrailRun[] {
  if (!text) return []
  try {
    const raw = JSON.parse(text) as [string, string, string, number | null, string, number, string][]
    return raw.map(([from, to, worklist, worklistId, holder, sys, rule]) => ({
      from,
      to,
      worklist,
      worklistId: worklistId ?? null,
      holder,
      system: sys === 1,
      rule: rule === 'left-next' || rule === 'left-kept' ? rule : '',
    }))
  } catch {
    return []
  }
}

/**
 * Did the expected list cover the claim's facility at the bill month's end, on the
 * assignment history (build_bundle coverage_at, the #206 test)? `carried`: it had been
 * on it and was off by then, so only a carried-forward setting put the claim there.
 */
export type Coverage = 'unknown' | 'covered' | 'carried' | 'never'

const COVERAGE: readonly Coverage[] = ['unknown', 'covered', 'carried', 'never']

/**
 * The claim's row in Rand's calc.predicted_worklist_periods (claims.json `ep`): who the
 * period table expected to work it at billing. The worklist is the claim's predicted
 * worklist (FactClaims takes it from this table).
 */
export interface ExpectedPeriod {
  /** The period's first and last day, 'YYYY-MM-DD'. */
  from: string
  to: string
  priority: number | null
  /** How many worklists matched; the highest priority won. */
  lists: number | null
  coverage: Coverage
  /** With `carried`: the last day the list was on the facility. */
  lastOn: string
  /** The period's person: '' in the row means the claim's predicted person. */
  person: string
}

export function parseExpected(text: string | undefined | null, predicted: string): ExpectedPeriod | null {
  if (!text) return null
  try {
    const [from, to, priority, lists, cover, lastOn, person] = JSON.parse(text) as [
      string, string, number | null, number | null, number, string, string,
    ]
    return {
      from,
      to,
      priority: priority ?? null,
      lists: lists ?? null,
      coverage: COVERAGE[cover] ?? 'unknown',
      lastOn: lastOn ?? '',
      person: person || predicted,
    }
  } catch {
    return null
  }
}

/** The first real worklist and person that held the claim from its bill month on. */
export interface FirstWorker {
  worklist: string
  worklistId: number | null
  /**
   * Held by a person with no worklist: Medevolve's "Unassigned" (or "No Worklist") with
   * a user on it. The person is real (the month counts in their pile); the list is not.
   */
  noList: boolean
  person: string
  /** The first month end it was there, 'YYYY-MM'. */
  month: string
  /** Days from the bill date to that month end: it got there within this many. */
  days: number | null
}

/** A worklist name that means "no worklist" rather than a list (not a system list). */
const NO_LIST = /^(unassigned|no worklist)?$/i

/**
 * Off the month-end trail (FactOpenClaims): the first month end on or after billing at
 * which a person held the claim and it was not on a system list. Month ends only, so
 * the days are an upper bound. null when no month end shows one.
 *
 * A claim can sit on "Unassigned" with a user on it (visit 510-1117137, Aug 2026): the
 * person counts, the list does not, so the comparison is on the person alone.
 */
export function firstWorker(trail: readonly TrailRun[], billDate: string): FirstWorker | null {
  const billMonth = billDate.slice(0, 7)
  const run = [...trail]
    .sort((a, b) => a.from.localeCompare(b.from))
    .find((r) => !r.system && r.holder !== 'Unassigned' && r.holder !== '' && r.to >= billMonth)
  if (!run) return null
  const month = run.from > billMonth ? run.from : billMonth
  const [y, m] = month.split('-').map(Number) as [number, number]
  const end = Date.UTC(y, m, 0)
  const billed = /^\d{4}-\d{2}-\d{2}/.test(billDate) ? Date.parse(`${billDate.slice(0, 10)}T00:00:00Z`) : NaN
  return {
    worklist: run.worklist,
    worklistId: run.worklistId,
    noList: NO_LIST.test(run.worklist.trim()),
    person: run.holder,
    month,
    days: Number.isNaN(billed) ? null : Math.max(0, Math.round((end - billed) / 86_400_000)),
  }
}

/** The person the caller may not see, as /api/claims writes it (api/shared/touches.ts OUTSIDE_SCOPE). */
export const OUTSIDE_ACCESS = 'Outside your access'

/** Expected against actual, in words: same, a different person, or not knowable. */
export function expectedVsActual(expected: ExpectedPeriod, worklist: string, actual: FirstWorker): string {
  if (expected.person === OUTSIDE_ACCESS || actual.person === OUTSIDE_ACCESS)
    return 'Can’t compare: one of the two is outside your access'
  if (!expected.person) return 'Nobody was expected to compare with'
  if (expected.person !== actual.person) return 'Different person'
  if (actual.noList) return 'Same person as expected'
  return actual.worklist.trim().toLowerCase() === worklist.trim().toLowerCase()
    ? 'Same as expected'
    : 'Same person as expected, on another list'
}

/** Whose open pile a run's months count in, on this app's rule, in words. */
export function pileOf(run: TrailRun): string {
  if (run.system) return 'Nobody: a system list is no one’s pile'
  if (run.holder === 'Unassigned') return 'Nobody: no one held it'
  return run.holder
}

/**
 * A co-insurance claim's outcome under the follows-primary rule (from 2026-10-12): the
 * model's `CoinsRule`, claims.json's `cr`. '' when the claim is not co-insurance, or the
 * build did not read the model's reason.
 */
export type CoinsOutcome = '' | 'FollowsPrimary' | 'PrimaryUnassigned' | 'NoPrimaryClaim' | 'PrimaryNotInModel'

const COINS: readonly string[] = ['FollowsPrimary', 'PrimaryUnassigned', 'NoPrimaryClaim', 'PrimaryNotInModel']

/**
 * Step 3's words: why the claim went where it did, as the model recorded it. Never
 * inferred from the credited person matching the predicted one: a claim that followed
 * its primary to a person who was also its own prediction looks predicted that way
 * (visit 273-2198017). `primary` is where the words name the primary claim, for the
 * panel to link; `before` / `after` the words either side of it.
 */
export function creditReason(claim: ClaimAttribution): {
  before: string
  primary: string
  after: string
} | null {
  const own =
    claim.rule === 'predicted'
      ? 'its own predicted worklist'
      : claim.rule === 'owner'
        ? 'its own rule: no prediction, so the owner of the first real worklist it was on'
        : 'its own rule, below'
  const id = claim.primaryClaim
  switch (claim.coins) {
    case 'FollowsPrimary':
      return id
        ? { before: 'Followed the primary claim ', primary: id, after: ' (same patient, facility, service month), credited to its owner.' }
        : { before: 'Followed its primary claim (same patient, facility, service month), credited to its owner.', primary: '', after: '' }
    case 'PrimaryUnassigned':
      return id
        ? { before: 'Primary claim ', primary: id, after: ` has no assignee: credited by ${own}.` }
        : { before: `Primary claim has no assignee: credited by ${own}.`, primary: '', after: '' }
    case 'NoPrimaryClaim':
      return { before: `No primary claim that month: credited by ${own}.`, primary: '', after: '' }
    case 'PrimaryNotInModel':
      return id
        ? { before: 'Primary claim ', primary: id, after: ` is not in the model: credited by ${own}.` }
        : { before: `Primary claim is not in the model: credited by ${own}.`, primary: '', after: '' }
    default:
      return null
  }
}

export interface ClaimAttribution {
  /** Who it is credited to: the row's owner. */
  owner: string
  billDate: string
  worklist: string
  worklistId: number | null
  /** '' when the matcher predicted nobody, or the person is not in the employee list. */
  predicted: string
  rule: AttributionRule | null
  /** The model's AttrSource, as written; '' on a build without it. */
  source: string
  coins: CoinsOutcome
  /** The co-insurance claim's primary claim, `<facility id>-<visit>`; '' for none. */
  primaryClaim: string
  trail: TrailRun[]
  /**
   * What the period table expected at billing; null when the claim has none, and
   * undefined on a build without the column (the step is then left out).
   */
  expected?: ExpectedPeriod | null
}

/** Whether the rows carry the attribution columns (a build from 2026-10-12 on). */
export const hasAttribution = (data: ClaimDetailData) => !!(data.cols.pw && data.dict.pw)

export function attributionOf(data: ClaimDetailData, i: number): ClaimAttribution {
  const { dict, cols } = data
  const pick = (d: string[] | undefined, c: number[] | undefined) =>
    d && c ? (d[c[i]!] ?? '') : ''
  const rule = pick(dict.ab, cols.ab)
  const coins = pick(dict.cr, cols.cr)
  const predicted = pick(dict.pu, cols.pu)
  return {
    owner: dict.e[cols.e[i]!] ?? '',
    billDate: dict.d[cols.d[i]!] ?? '',
    worklist: pick(dict.pw, cols.pw),
    worklistId: cols.pwk?.[i] ?? null,
    predicted,
    rule: rule in RULE_TEXT ? (rule as AttributionRule) : null,
    source: pick(dict.as, cols.as),
    coins: COINS.includes(coins) ? (coins as CoinsOutcome) : '',
    primaryClaim: pick(dict.cp, cols.cp),
    trail: parseTrail(pick(dict.ot, cols.ot)),
    ...(dict.ep && cols.ep ? { expected: parseExpected(pick(dict.ep, cols.ep), predicted) } : {}),
  }
}

/**
 * The row of a claim id (`<facility id>-<visit>`, as calc.claim_attribution names a
 * primary claim) in the caller's rows, or null when it is not among them: billed outside
 * the period, or owned by someone the caller may not see.
 */
export function rowOfClaim(data: ClaimDetailData, claimId: string): number | null {
  const dash = claimId.indexOf('-')
  if (dash < 1) return null
  const fid = Number(claimId.slice(0, dash))
  const visit = claimId.slice(dash + 1)
  const { cols } = data
  for (let i = 0; i < cols.v.length; i++)
    if (cols.v[i] === visit && (cols.fid?.[i] ?? fid) === fid) return i
  return null
}
